// src/components/admin/dsat/reports/SearchableStudentDropdown.js
import React, { useEffect, useMemo, useRef, useState } from "react";

const ui = {
  container: {
    position: "relative",
    width: "100%",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },

  trigger: (disabled) => ({
    width: "100%",
    minHeight: 44,
    height: 46,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    boxSizing: "border-box",
    opacity: disabled ? 0.6 : 1,
    userSelect: "none",
  }),

  triggerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    flex: 1,
  },

  avatar: {
    width: 28,
    height: 28,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 900,
    color: "#1d4ed8",
    background: "linear-gradient(145deg, #eef2ff, #dbeafe)",
    border: "1px solid #bfdbfe",
    flexShrink: 0,
  },

  triggerTextWrap: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  triggerPrimary: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.2,
  },
  triggerSecondary: {
    fontSize: 12,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.2,
  },
  placeholder: {
    fontSize: 14,
    color: "#64748b",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  caret: (open) => ({
    fontSize: 12,
    color: "#64748b",
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 120ms ease",
    flexShrink: 0,
  }),

  dropdown: {
    position: "absolute",
    top: "110%",
    left: 0,
    right: 0,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
    zIndex: 9999,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  searchBar: {
    padding: 10,
    borderBottom: "1px solid #eef2f7",
    background: "linear-gradient(180deg, #ffffff, #f8fafc)",
  },

  searchInput: {
    width: "100%",
    padding: "9px 10px",
    borderRadius: 10,
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: 14,
    boxSizing: "border-box",
  },

  list: {
    // ✅ long dropdown
    maxHeight: 360,
    overflowY: "auto",
    padding: 6,
    background: "#ffffff",
  },

  option: (active, selected) => ({
    borderRadius: 12,
    padding: "10px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    border: selected ? "1px solid #bfdbfe" : "1px solid transparent",
    background: selected
      ? "linear-gradient(145deg, #eff6ff, #eef2ff)"
      : active
      ? "#f8fafc"
      : "transparent",
    transition: "background 90ms ease, border 90ms ease",
  }),

  optionLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    flex: 1,
  },

  optionText: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  optionName: (selected) => ({
    fontSize: 14,
    fontWeight: selected ? 800 : 700,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.2,
  }),

  optionEmail: {
    fontSize: 12,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.2,
  },

  optionRight: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
  },

  check: {
    width: 18,
    height: 18,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#1d4ed8",
    color: "#fff",
    fontSize: 12,
    fontWeight: 900,
  },

  empty: {
    padding: 14,
    fontSize: 13,
    color: "#94a3b8",
    textAlign: "center",
  },
};

function initialFromName(name) {
  const s = (name || "").trim();
  if (!s) return "?";
  return s[0].toUpperCase();
}

export default function SearchableStudentDropdown({
  students = [],
  selectedId,
  onSelect,
  placeholder = "Select a student…",
  disabled = false,
  getSearchText,
  getTriggerSecondaryText,
  getOptionSecondaryText,
  getOptionRightText,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedStudent = useMemo(
    () => students.find((s) => s.uid === selectedId) || null,
    [students, selectedId]
  );

  const filteredStudents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return students; // keep recency ordering passed in
    return students.filter((s) => {
      const haystack = String(
        getSearchText
          ? getSearchText(s)
          : `${s.name || ""} ${s.email || ""}`
      ).toLowerCase();
      return haystack.includes(term);
    });
  }, [students, searchTerm, getSearchText]);

  // When opening, focus search and reset active index
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
      // defer focus slightly so DOM exists
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearchTerm("");
    }
  }, [isOpen]);

  // Clamp active index if filtered list changes
  useEffect(() => {
    if (activeIndex >= filteredStudents.length) setActiveIndex(0);
  }, [filteredStudents.length, activeIndex]);

  const close = () => setIsOpen(false);

  const handleSelect = (s) => {
    onSelect?.(s.uid);
    close();
    setSearchTerm("");
  };

  const onTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((v) => !v);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(0);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (filteredStudents.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filteredStudents.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = filteredStudents[activeIndex];
      if (s) handleSelect(s);
    }
  };

  return (
    <div style={ui.container} ref={containerRef}>
      {/* Trigger */}
      <div
        style={ui.trigger(disabled)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-expanded={isOpen}
        onClick={() => !disabled && setIsOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
      >
        <div style={ui.triggerLeft}>
          <div style={ui.avatar}>
            {initialFromName(selectedStudent?.name || "")}
          </div>

          {selectedStudent ? (
            <div style={ui.triggerTextWrap}>
              <div style={ui.triggerPrimary}>{selectedStudent.name}</div>
              <div style={ui.triggerSecondary}>
                {getTriggerSecondaryText
                  ? getTriggerSecondaryText(selectedStudent)
                  : `${selectedStudent.email || "No email"} • Last: ${selectedStudent.lastDateLabel || "—"}`}
              </div>
            </div>
          ) : (
            <div style={ui.placeholder}>{placeholder}</div>
          )}
        </div>

        <div style={ui.caret(isOpen)}>▾</div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div style={ui.dropdown}>
          <div style={ui.searchBar}>
            <input
              ref={inputRef}
              style={ui.searchInput}
              placeholder="Type to search…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>

          <div style={ui.list}>
            {filteredStudents.length === 0 ? (
              <div style={ui.empty}>No students found</div>
            ) : (
              filteredStudents.map((s, idx) => {
                const selected = s.uid === selectedId;
                const active = idx === activeIndex;
                const optionRightText = getOptionRightText
                  ? getOptionRightText(s)
                  : (s.lastDateLabel || "—");
                const showRightText =
                  optionRightText !== null &&
                  optionRightText !== undefined &&
                  optionRightText !== "";

                return (
                  <div
                    key={s.uid}
                    style={ui.option(active, selected)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()} // prevents input blur closing weirdly
                    onClick={() => handleSelect(s)}
                  >
                    <div style={ui.optionLeft}>
                      <div style={ui.avatar}>{initialFromName(s.name)}</div>
                      <div style={ui.optionText}>
                        <div style={ui.optionName(selected)}>{s.name}</div>
                        <div style={ui.optionEmail}>
                          {getOptionSecondaryText
                            ? getOptionSecondaryText(s)
                            : (s.email || "No email")}
                        </div>
                      </div>
                    </div>

                    <div style={ui.optionRight}>
                      {showRightText && <span>{optionRightText}</span>}
                      {selected && <span style={ui.check}>✓</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
